import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    Add,
    CheckCircleOutlined,
    DateRange,
    Delete,
    Edit,
    HighlightOff,
    RemoveCircleOutlineOutlined,
    Schedule,
} from '@mui/icons-material';
import { SeparationBar } from '../layout/components/SeparationBar.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { EmptyResults } from '../shared-mui-components/ListMessages.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerErrorEffect,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Link, Outlet, useNavigate, useOutletContext } from 'react-router';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { type JSX, useState } from 'react';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

interface OperatorOffstreetEventsResult {
    events: OperatorOffstreetEvent[];
    eventProductOptions: OffstreetEventProductOption[];
}

interface OperatorOffstreetEvent {
    productEventId: string;
    contractTemplateId: number;
    status: OperatorEventStatus;
    eventName: string;
    productName: Message;
    validFrom: string;
    validTo: string;
    hasActiveProductTemplate: boolean;
    hasActiveStacks: boolean;
}

export interface OffstreetEventProductOption {
    contractTemplateId: number;
    productName: Message;
}

export const enum OperatorEventStatus {
    ACTIVE = 'ACTIVE',
    FUTURE = 'FUTURE',
    EXPIRED = 'EXPIRED',
    DELETED = 'DELETED',
}

export function OperatorOffstreetEvents() {
    const [offstreetEventsState, refetchOffstreetEvents] = useServerFetch<
        OperatorOffstreetEventsResult,
        object,
        null
    >(
        () => ({
            url: `/ui-api/operator-account/offstreet-events`,
        }),
        {},
    );
    const { currentLogin } = useOperatorContext();

    return (
        <MainContent>
            <OperatorAsyncLoadedSection
                requestState={offstreetEventsState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={offstreetEvents => (
                    <>
                        <HeaderWithButtons
                            title={
                                <Localized
                                    de="Veranstaltungen"
                                    fr="Manifestations"
                                    it="Eventi"
                                    en="Events"
                                />
                            }
                            icon={DateRange}
                            headerButtons={
                                offstreetEvents.eventProductOptions.length >
                                    0 &&
                                currentLogin.permissions.eventsWrite ? (
                                    <Link to={'add'}>
                                        <Button
                                            variant="contained"
                                            startIcon={<Add />}
                                            color="primary"
                                        >
                                            <Localized
                                                de="Neue Veranstaltung"
                                                fr="Nouvelle manifestation"
                                                it="Nuovo evento"
                                                en="New event"
                                            />
                                        </Button>
                                    </Link>
                                ) : undefined
                            }
                        />
                        <SeparationBar />
                        <OperatorOffstreetEventTable
                            offstreetEvents={offstreetEvents.events}
                            refetchOffstreetEvents={refetchOffstreetEvents}
                        />
                        <OffstreetEventsOutlet
                            context={{
                                events: offstreetEvents.events,
                                refetchEvents: refetchOffstreetEvents,
                                productOptions:
                                    offstreetEvents.eventProductOptions,
                            }}
                        />
                    </>
                )}
            />
        </MainContent>
    );
}

function OperatorOffstreetEventTable({
    offstreetEvents,
    refetchOffstreetEvents,
}: {
    offstreetEvents: OperatorOffstreetEvent[];
    refetchOffstreetEvents: () => void;
}) {
    const navigate = useNavigate();
    const { currentLogin } = useOperatorContext();
    const [deleteEventInfo, setDeleteEventInfo] =
        useState<OperatorOffstreetEvent | null>(null);

    const [deleteOffstreetEventState, deleteOffstreetEvent] = useServerWrite<
        { eventId: string },
        null,
        ValidationData
    >(({ eventId }) => ({
        url: `/ui-api/operator-account/offstreet-events/${eventId}`,
        method: RequestMethod.DELETE,
    }));
    useServerSuccessEffect(deleteOffstreetEventState, () => {
        setDeleteEventInfo(null);
        refetchOffstreetEvents();
    });
    useServerErrorEffect(deleteOffstreetEventState, () => {
        setDeleteEventInfo(null);
    });

    if (offstreetEvents.length === 0) {
        return <EmptyResults hasFilter={false} />;
    }
    return (
        <>
            <BackendRequestErrorMessage
                requestState={deleteOffstreetEventState}
            />
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Localized
                                    de="Status"
                                    fr="Statut"
                                    it="Stato"
                                    en="Status"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Veranstaltung"
                                    fr="Événement"
                                    it="Evento"
                                    en="Event"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Produktbezeichnung"
                                    fr="Désignation du produit"
                                    it="Descrizione prodotto"
                                    en="Product description"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Beginn"
                                    fr="Début"
                                    it="Inizio"
                                    en="Start"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Ende"
                                    fr="Fin"
                                    it="Fine"
                                    en="End"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <Localized
                                    de="Aktionen"
                                    fr="Actions"
                                    it="Azioni"
                                    en="Actions"
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {offstreetEvents.map(offstreetEvent => (
                            <TableRow
                                key={offstreetEvent.productEventId}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                    navigate(`${offstreetEvent.productEventId}`)
                                }
                            >
                                <TableCell>
                                    <OperatorOffstreetEventStatusIcon
                                        status={offstreetEvent.status}
                                    />
                                </TableCell>
                                <TableCell>
                                    {offstreetEvent.eventName}
                                </TableCell>
                                <TableCell>
                                    <Localized
                                        {...offstreetEvent.productName}
                                    />
                                </TableCell>
                                <TableCell>
                                    {Formatter.dayMonthYearHourMinute(
                                        DateTime.fromISO(
                                            offstreetEvent.validFrom,
                                        ),
                                    )}
                                </TableCell>
                                <TableCell>
                                    {Formatter.dayMonthYearHourMinute(
                                        DateTime.fromISO(
                                            offstreetEvent.validTo,
                                        ),
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    {isEventEditable(
                                        offstreetEvent.status,
                                        offstreetEvent.hasActiveProductTemplate,
                                    ) &&
                                        currentLogin.permissions
                                            .eventsWrite && (
                                            <IconButton
                                                color="primary"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    navigate(
                                                        `${offstreetEvent.productEventId}/edit`,
                                                    );
                                                }}
                                            >
                                                <Edit />
                                            </IconButton>
                                        )}
                                    {offstreetEvent.status ===
                                        OperatorEventStatus.FUTURE &&
                                        !offstreetEvent.hasActiveStacks &&
                                        currentLogin.permissions
                                            .eventsWrite && (
                                            <Tooltip
                                                title={
                                                    <Localized
                                                        de="löschen"
                                                        fr="supprimer"
                                                        it="elimina"
                                                        en="delete"
                                                    />
                                                }
                                            >
                                                <IconButton
                                                    color="primary"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setDeleteEventInfo(
                                                            offstreetEvent,
                                                        );
                                                    }}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {deleteEventInfo !== null && (
                    <DeleteOffstreetEventModal
                        offstreetEvent={deleteEventInfo}
                        deleteOffstreetEventState={deleteOffstreetEventState}
                        onDelete={() =>
                            deleteOffstreetEvent({
                                eventId: deleteEventInfo?.productEventId,
                            })
                        }
                        onClose={() => setDeleteEventInfo(null)}
                    />
                )}
            </TableContainer>
        </>
    );
}

function DeleteOffstreetEventModal({
    offstreetEvent,
    deleteOffstreetEventState,
    onDelete,
    onClose,
}: {
    offstreetEvent: OperatorOffstreetEvent;
    deleteOffstreetEventState: ServerRequestState<null, ValidationData>;
    onDelete: () => void;
    onClose: () => void;
}) {
    return (
        <ConfirmationDialog
            open={true}
            title={
                <Localized
                    de="Veranstaltung löschen"
                    fr="Supprimer la manifestation"
                    it="Eliminare l’evento"
                    en="Delete event"
                />
            }
            confirmLabel={
                <Localized de="Löschen" fr="Effacer" it="Elimina" en="Delete" />
            }
            confirmDisabled={
                deleteOffstreetEventState.status === RequestStatus.PENDING
            }
            onConfirm={onDelete}
            onCancel={onClose}
            dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
        >
            <p>
                <Localized
                    de={`Möchten Sie die Veranstaltung ${offstreetEvent.eventName} löschen?`}
                    fr={`Souhaitez-vous supprimer la manifestation ${offstreetEvent.eventName} ?`}
                    it={`Desideri eliminare l’evento ${offstreetEvent.eventName}?`}
                    en={`Would you like to delete the event ${offstreetEvent.eventName}?`}
                />
            </p>
        </ConfirmationDialog>
    );
}

export function OperatorOffstreetEventStatusIcon({
    status,
}: {
    status: OperatorEventStatus;
}): JSX.Element {
    switch (status) {
        case OperatorEventStatus.ACTIVE:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="gültig"
                            fr="valable"
                            it="valido"
                            en="valid"
                        />
                    }
                    placement="right"
                >
                    <CheckCircleOutlined color="success" />
                </Tooltip>
            );
        case OperatorEventStatus.FUTURE:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="zukünftig gültig"
                            fr="Valable dans le futur"
                            it="valido in futuro"
                            en="Valid in the future"
                        />
                    }
                    placement="right"
                >
                    <Schedule color="warning" />
                </Tooltip>
            );
        case OperatorEventStatus.EXPIRED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="abgelaufen"
                            fr="expirée"
                            it="scaduta"
                            en="expired"
                        />
                    }
                    placement="right"
                >
                    <RemoveCircleOutlineOutlined color="disabled" />
                </Tooltip>
            );
        case OperatorEventStatus.DELETED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="gelöscht"
                            fr="supprimé"
                            it="cancellato"
                            en="deleted"
                        />
                    }
                    placement="right"
                >
                    <HighlightOff color="error" />
                </Tooltip>
            );
    }
}

export function isEventEditable(
    status: OperatorEventStatus,
    hasActiveProductTemplate: boolean,
) {
    switch (status) {
        case OperatorEventStatus.ACTIVE:
        case OperatorEventStatus.FUTURE:
            return hasActiveProductTemplate;
        case OperatorEventStatus.EXPIRED:
        case OperatorEventStatus.DELETED:
            return false;
    }
}

export function useOffstreetEventsOutlet() {
    return useOutletContext<OffstreetEventsOutletContext>();
}

export function OffstreetEventsOutlet({
    context,
}: {
    context: OffstreetEventsOutletContext;
}) {
    return <Outlet context={context} />;
}

export interface OffstreetEventsOutletContext {
    events: OperatorOffstreetEvent[];
    refetchEvents: () => void;
    productOptions: OffstreetEventProductOption[];
}
